import { Extension, type Extensions } from '@tiptap/core';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { Markdown } from '@tiptap/markdown';
import StarterKit from '@tiptap/starter-kit';
import { RefLink } from './refLink';
import { RefSpan } from './refSpan';
import { TimeMark } from './timeMark';

/**
 * Bausteine des Texteditors. Der Nutzer schreibt formatiert (Zeilentyp, fett, kursiv, Listen); gespeichert wird Markdown.
 * Alles, was bisher in Markdown möglich war (Zitate, Code, Durchgestrichenes, Aufgaben, Links, Trennlinien), bleibt erhalten.
 */
export function richTextExtensions(extra: Extensions = [], isKnown: (key: string) => boolean = () => false): Extensions {
  return [
    StarterKit.configure({
      underline: false, // gibt es in Markdown nicht
      link: { openOnClick: false, autolink: false },
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    RefLink.configure({ isKnown, titles: false }),
    // Zeitmarken (PLAN.md §16): nach RefLink, vor RefSpan – ein Bezug umschließt die Zeitmarken in ihm
    TimeMark,
    // nach RefLink: ein Bezug umschließt die Nennungen in ihm (Reihenfolge der Markierungen im Schema)
    RefSpan,
    Markdown,
    ...extra,
  ];
}

/** Bausteine einfacher Textfelder (Kurzbeschreibung u. a.): nur Text, Zeilenumbrüche und verknüpfte Wörter; Enter macht einen Umbruch. */
export function fieldTextExtensions(extra: Extensions = [], isKnown: (key: string) => boolean = () => false): Extensions {
  return [
    StarterKit.configure({
      heading: false,
      bold: false,
      italic: false,
      strike: false,
      code: false,
      codeBlock: false,
      blockquote: false,
      bulletList: false,
      orderedList: false,
      listItem: false,
      listKeymap: false,
      horizontalRule: false,
      link: false,
      underline: false,
    }),
    Extension.create({
      name: 'enterAsBreak',
      addKeyboardShortcuts() {
        return { Enter: () => this.editor.commands.setHardBreak() };
      },
    }),
    RefLink.configure({ isKnown }),
    ...extra,
  ];
}

/**
 * Markdown aus dem Editor. Unterstriche mitten im Wort maskiert der Editor (`anna\_berg`); das ist in Markdown unnötig und würde
 * Verweise wie `@anna_berg` zerstören, deshalb wird es zurückgenommen.
 */
export function cleanMarkdown(md: string): string {
  // die freie Schlusszeile des Editors gehört nicht ins Dokument
  return md
    .replace(/(\w)\\_(?=\w)/g, '$1_')
    .replace(/\\\[(@[A-Za-z][A-Za-z0-9_]{0,29})\\\]/g, '[$1]') // Verweise [@schlüssel] nicht maskieren
    .replace(/\s+$/, '');
}
